// 🎬 Service API - Pop Movie
// Abstraction qui gère l'ancien PHP ET la nouvelle API Laravel

import {API_CONFIG} from '../config/apiConfig';

/**
 * Service principal pour gérer les appels API
 * Supporte à la fois l'ancien système PHP et la nouvelle API Laravel
 */
class PopMovieAPIService {

    getToken() {
        return localStorage.getItem('cinestream_token');
    }

    /**
     * Récupérer la liste des films
     * @param {Object} options - Options de filtrage
     * @param {boolean} options.includeInactive - Inclure les films inactifs (admin only)
     * @param {number} options.timestamp - Timestamp personnalisé pour le cache-busting (optionnel, sera généré si non fourni)
     * @returns {Promise<Array>} Liste des films
     */
    async getMovies({ includeInactive = false, timestamp = null } = {}) {
        const token = this.getToken();

        // Le catalogue pèse ~2 300 entrées, distributions comprises, et il est
        // identique d'une visite à l'autre neuf fois sur dix. On le laisse donc
        // passer par le cache HTTP au lieu de le retélécharger à chaque montage.
        //
        // Un `timestamp` explicite veut dire « je veux du frais, maintenant »
        // (retour d'un ajout, d'un import, d'une modification admin) : là on
        // court-circuite le cache comme avant.
        //
        // Sans timestamp, `Cache-Control: max-age=60` en *requête* borne nous-
        // mêmes la péremption : le navigateur ne réutilise une réponse que si
        // elle a moins d'une minute, sinon il revalide. Ça vaut même si le
        // backend n'envoie encore aucun en-tête de cache — et le jour où il
        // renvoie un ETag, la revalidation devient un 304 vide.
        const forceFresh = timestamp !== null;
        const url = forceFresh
            ? `${API_CONFIG.NEW_API_BASE}/movies?_t=${timestamp}`
            : `${API_CONFIG.NEW_API_BASE}/movies`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...API_CONFIG.DEFAULT_HEADERS,
                'Cache-Control': forceFresh ? 'no-cache' : 'max-age=60',
                'X-Admin-Token': token,
            },
            cache: forceFresh ? 'reload' : 'default',
        });

        if (!response.ok) {
            throw new Error(`Erreur API: ${response.status}`);
        }

        const data = await response.json();

        // Laravel retourne un format paginé: { data: [...], meta: {...} }
        // On retourne juste le tableau de films pour garder la compatibilité
        return data.data || data;
    }

    /**
     * Récupérer un film par son ID
     * @param {string|number} id - ID du film
     * @returns {Promise<Object>} Détails du film
     */
    async getMovieById(id) {
        // API LARAVEL
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/${id}`, {
            method: 'GET',
            headers: API_CONFIG.DEFAULT_HEADERS,
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error(`Film introuvable: ${id}`);
        }

        return await response.json();
    }

    /**
     * Récupérer un film par son UUID
     * @param {string} uuid - UUID du film
     * @returns {Promise<Object>} Détails du film
     */
    async getMovieByUuid(uuid) {
        // API LARAVEL
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/uuid/${uuid}`, {
            method: 'GET',
            headers: API_CONFIG.DEFAULT_HEADERS,
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error(`Film introuvable: ${uuid}`);
        }

        return await response.json();
    }

    /**
     * Rechercher des films
     * @param {string} query - Texte de recherche
     * @returns {Promise<Array>} Films correspondants
     */
    async searchMovies(query) {
        // API LARAVEL
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/search?q=${encodeURIComponent(query)}`, {
            method: 'GET',
            headers: API_CONFIG.DEFAULT_HEADERS,
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error('Erreur lors de la recherche');
        }

        const data = await response.json();
        return data.results || [];
    }

    /**
     * Récupérer les films mis en avant
     * @param {string} type - 'film' ou 'serie' (optionnel)
     * @returns {Promise<Array>} Films featured
     */
    async getFeaturedMovies(type = null) {
        // API LARAVEL
        const params = type ? `?type=${type}` : '';
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/featured${params}`, {
            method: 'GET',
            headers: API_CONFIG.DEFAULT_HEADERS,
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error('Erreur lors de la récupération des films featured');
        }

        return await response.json();
    }

    /**
     * Récupérer tous les épisodes d'une série
     * @param {number} tmdbId - ID TMDB de la série
     * @returns {Promise<Object>} Infos série + épisodes groupés par saison
     */
    async getSerieEpisodes(tmdbId) {
        // API LARAVEL
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/series/${tmdbId}/episodes`, {
            method: 'GET',
            headers: API_CONFIG.DEFAULT_HEADERS,
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error('Série introuvable');
        }

        return await response.json();
    }

    /**
     * Récupérer la liste des genres disponibles
     * @returns {Promise<Array>} Liste des genres
     */
    async getGenres() {
        // API LARAVEL
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/genres`, {
            method: 'GET',
            headers: API_CONFIG.DEFAULT_HEADERS,
            cache: 'no-cache',
        });

        if (!response.ok) {
            throw new Error('Erreur lors de la récupération des genres');
        }

        const data = await response.json();
        return data.genres || [];
    }

    // =====================================
    // 🎬 PROGRESSION
    // =====================================

    async getProgress(userId, movieId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/progress?user_id=${userId}&movie_id=${movieId}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken()
            }
        });
        return await response.json();
    }

    async getProgressList(userId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/progress/list?user_id=${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken()
            }
        });
        return await response.json();
    }

    async saveProgress(userId, movieId, position, duration, percentage, movieTitle = '') {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/progress`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken(),
            },
            body: JSON.stringify({
                action: 'save',
                user_id: userId,
                movie_id: movieId,
                movie_title: movieTitle,
                position,
                duration,
                percentage,
            })
        });
        return await response.json();
    }

    async removeProgress(userId, movieId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/progress`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken(),
            },
            body: JSON.stringify({
                action: 'remove',
                user_id: userId,
                movie_id: movieId,
            })
        });
        return await response.json();
    }

    saveProgressBeacon(userId, movieId, position, duration, percentage, movieTitle = '') {
        const data = JSON.stringify({
            action: 'save',
            user_id: userId,
            movie_id: movieId,
            movie_title: movieTitle,
            position,
            duration,
            percentage,
        });

        const blob = new Blob([data], { type: 'application/json' });
        const url = `${API_CONFIG.NEW_API_BASE}/progress?token=${encodeURIComponent(this.getToken())}`;

        return navigator.sendBeacon(url, blob);
    }

    // =====================================
    // 🎬 FILMS VUS
    // =====================================

    async getWatchedMovies(userId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watched?user_id=${userId}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken()
            }
        });
        return await response.json();
    }

    async addWatchedMovie(userId, movieId, movieTitle = '') {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watched`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken(),
            },
            body: JSON.stringify({
                action: 'add',
                user_id: userId,
                movie_id: movieId,
                movie_title: movieTitle,
            })
        });
        return await response.json();
    }

    async removeWatchedMovie(userId, movieId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/watched`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken(),
            },
            body: JSON.stringify({
                action: 'remove',
                user_id: userId,
                movie_id: movieId,
            })
        });
        return await response.json();
    }

    // =====================================
    // 🎬 SESSIONS
    // =====================================

    async sendSessionPing(payload) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/sessions/ping`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken(),
            },
            body: JSON.stringify(payload)
        });
        return await response.json();
    }

    async getActiveSessions() {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/sessions/active`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken()
            }
        });
        return await response.json();
    }

    /**
     * Vérifier le statut IP (banned/locked/allowed)
     */
    async checkIpStatus() {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/check-ip-status`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur checkIpStatus:', error);
            throw error;
        }
    }

    /**
     * Login avec rate limiting
     */
    async login(username, password) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            // Vérifier le status AVANT de parser le JSON
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));

                // Créer une erreur avec toutes les infos
                const error = new Error(errorData.message || `HTTP ${response.status}`);
                error.response = {
                    status: response.status,
                    statusText: response.statusText,
                    data: errorData,
                    headers: response.headers,
                };

                throw error;
            }

            // ✅ Si OK, parser et retourner
            return await response.json();
        } catch (error) {
            // ✅ Re-throw l'erreur pour que Login.jsx puisse la catcher
            console.error('apiService.login error:', error);
            throw error;
        }
    }

    /**
     * Vérifier un token
     */
    async verifyToken(token) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/verify-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token }),
            });

            if (!response.ok) {
                // Le code doit remonter : l'appelant déconnecte sur un refus
                // explicite du serveur, et seulement sur celui-là.
                const error = new Error(`HTTP ${response.status}`);
                error.status = response.status;
                throw error;
            }

            return await response.json();
        } catch (error) {
            // Un 401 ici veut simplement dire « jeton périmé, reconnecte-toi » :
            // c'est un état normal, pas un incident. Le signaler en rouge dans
            // la console envoie chercher une panne qui n'existe pas.
            if (error.status !== 401 && error.status !== 403) {
                console.error('Erreur verifyToken:', error);
            }
            throw error;
        }
    }

    /**
     * Récupérer les logs de visionnage (admin uniquement)
     */
    async getVideoLogs(page = 1, perPage = 50) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/video-logs?page=${page}&per_page=${perPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getVideoLogs:', error);
            throw error;
        }
    }

    /**
     * Récupérer les statistiques de sécurité (admin uniquement)
     */
    async getSecurityStats() {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/security-stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getSecurityStats:', error);
            throw error;
        }
    }

    /**
     * Débloquer une IP lockée
     * @param {string} ip - L'adresse IP à débloquer
     */
    async unlockIp(ip) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/security-stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify({
                    action: 'unlock_ip',
                    ip: ip,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur unlockIp:', error);
            throw error;
        }
    }

    /**
     * Débloquer un username
     * @param {string} username - Le username à débloquer
     */
    async unlockUsername(username) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/security-stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify({
                    action: 'unlock_username',
                    username: username,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur unlockUsername:', error);
            throw error;
        }
    }

    /**
     * Débannir une IP
     * @param {string} ip - L'adresse IP à débannir
     */
    async unbanIp(ip) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/security-stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify({
                    action: 'unban_ip',
                    ip: ip,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur unbanIp:', error);
            throw error;
        }
    }

    /**
     * Bannir une IP manuellement
     * @param {string} ip - L'adresse IP à bannir
     * @param {string} reason - Raison du ban (optionnel)
     */
    async banIpManual(ip, reason = 'Ban manuel par administrateur') {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/security-stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify({
                    action: 'ban_ip_manual',
                    ip: ip,
                    reason: reason,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur banIpManual:', error);
            throw error;
        }
    }

    /**
     * Nettoyer toutes les tentatives échouées
     */
    async clearAllSecurityAttempts() {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/security-stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify({
                    action: 'clear_all',
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur clearAllSecurityAttempts:', error);
            throw error;
        }
    }

    /**
     * Récupérer les sessions actives d'un utilisateur
     */
    async getUserSessions(userId) {
        const currentToken = this.getToken();
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/sessions?user_id=${userId}&token=${currentToken}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': currentToken,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Déconnecter une session spécifique
     */
    // Prend la session entière, et envoie les deux désignations possibles.
    //
    // Le serveur récent identifie une session par `id` et ne divulgue plus
    // les jetons ; l'ancien attendait `token`. Envoyer les deux rend le
    // bouton insensible à l'ordre de déploiement des deux dépôts : celui qui
    // répond utilise le champ qu'il connaît, l'autre est simplement absent.
    async disconnectSession(session) {
        const currentToken = this.getToken();
        const payload = typeof session === 'object' && session !== null
            ? { id: session.id, token: session.token }
            : { id: session };

        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/sessions/disconnect?token=${currentToken}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': currentToken,
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            // Sans ça, un 403 ou un 400 remontait en « erreur de connexion au
            // serveur » : impossible de distinguer une panne d'un refus.
            const error = new Error(data?.message || data?.error || `HTTP ${response.status}`);
            error.status = response.status;
            throw error;
        }

        return data;
    }

    /**
     * Déconnecter toutes les sessions d'un utilisateur
     */
    async logoutAllSessions(userId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/sessions/disconnect-all`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': this.getToken(),
            },
            body: JSON.stringify({ user_id: userId }),
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Compter le nombre de sessions actives (optionnel)
     */
    async getActiveSessionsCount(userId) {
        const currentToken = this.getToken();
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/sessions/active-count?user_id=${userId}&token=${currentToken}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Admin-Token': currentToken,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
    }


    /**
     * Récupérer tous les utilisateurs (sans passwords)
     * @returns {Promise<Object>} { success: true, users: [...] }
     */
    async getUsers() {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getUsers:', error);
            throw error;
        }
    }

    /**
     * Récupérer un utilisateur par ID
     * @param {number} userId - ID de l'utilisateur
     * @returns {Promise<Object>} { success: true, user: {...} }
     */
    async getUserById(userId) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users/${userId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getUserById:', error);
            throw error;
        }
    }

    /**
     * Créer un nouvel utilisateur
     * @param {Object} userData - { username, email, password, role }
     * @returns {Promise<Object>} { success: true, message: "...", user: {...} }
     */
    async createUser(userData) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify(userData),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur createUser:', error);
            throw error;
        }
    }

    /**
     * Modifier un utilisateur
     * @param {number} userId - ID de l'utilisateur
     * @param {Object} userData - { username, email, password, role }
     * @returns {Promise<Object>} { success: true, message: "...", user: {...} }
     */
    async updateUser(userId, userData) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify(userData),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur updateUser:', error);
            throw error;
        }
    }

    /**
     * Supprimer un utilisateur
     * @param {number} userId - ID de l'utilisateur
     * @returns {Promise<Object>} { success: true, message: "..." }
     */
    async deleteUser(userId) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur deleteUser:', error);
            throw error;
        }
    }

    /**
     * Activer/Désactiver un utilisateur
     * @param {number} userId - ID de l'utilisateur
     * @returns {Promise<Object>} { success: true, message: "...", user: {...} }
     */
    async inviteUser(userData) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Admin-Token': this.getToken() },
            body: JSON.stringify(userData),
        });
        return await response.json();
    }

    async generateResetLink(userId) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users/${userId}/generate-reset-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Admin-Token': this.getToken() },
        });
        return await response.json();
    }

    async validateInviteToken(token) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/invite/validate?token=${encodeURIComponent(token)}`, {
            headers: { 'Accept': 'application/json' },
        });
        return await response.json();
    }

    async completeSetup(token, password, passwordConfirmation) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/invite/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ token, password, password_confirmation: passwordConfirmation }),
        });
        return await response.json();
    }

    async getDiscordLinkStatus() {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/discord-link/status`, {
            headers: { 'Accept': 'application/json', 'X-Admin-Token': this.getToken() },
        });
        return await response.json();
    }

    async confirmDiscordLink(code) {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/discord-link/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Admin-Token': this.getToken() },
            body: JSON.stringify({ code }),
        });
        return await response.json();
    }

    async unlinkDiscord() {
        const response = await fetch(`${API_CONFIG.NEW_API_BASE}/discord-link/unlink`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Admin-Token': this.getToken() },
        });
        return await response.json();
    }

    async toggleUserStatus(userId) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/users/${userId}/toggle-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur toggleUserStatus:', error);
            throw error;
        }
    }

    /**
     * Voter sur un film (like ou dislike)
     * @param {string} movieId - ID du film
     * @param {string} rating - 'like' ou 'dislike'
     * @returns {Promise<Object>} { success: true, data: { user_rating, stats } }
     */
    async rateMovie(movieId, rating) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/${movieId}/rate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
                body: JSON.stringify({ rating }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur rateMovie:', error);
            throw error;
        }
    }

    /**
     * Retirer un vote sur un film
     * @param {string} movieId - ID du film
     * @returns {Promise<Object>} { success: true, data: { user_rating: null, stats } }
     */
    async unrateMovie(movieId) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/${movieId}/rate`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur unrateMovie:', error);
            throw error;
        }
    }

    /**
     * Récupérer les statistiques de vote d'un film
     * @param {string} movieId - ID du film
     * @returns {Promise<Object>} { success: true, data: { stats: {likes, dislikes, total}, user_rating } }
     */
    async getMovieRating(movieId) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/movies/${movieId}/rating`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getMovieRating:', error);
            throw error;
        }
    }

    /**
     * Récupérer les films aimés par l'utilisateur
     * @returns {Promise<Object>} { success: true, data: [...] }
     */
    async getUserLikedMovies() {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/user/liked-movies`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getUserLikedMovies:', error);
            throw error;
        }
    }

    /**
     * Récupérer les films dislikés par l'utilisateur
     * @returns {Promise<Object>} { success: true, data: [...] }
     */
    async getUserDislikedMovies() {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/user/disliked-movies`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getUserDislikedMovies:', error);
            throw error;
        }
    }

    // =====================================
    // ✨ RECOMMANDATIONS
    // =====================================

    /**
     * Récupérer les films recommandés pour l'utilisateur
     * @param {number} limit - Nombre de films à récupérer (par défaut 20)
     * @returns {Promise<Object>} { success: true, data: [...] }
     */
    async getRecommendations(limit = 20) {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/recommendations?limit=${limit}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Token': this.getToken(),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur getRecommendations:', error);
            throw error;
        }
    }

    /**
     * Récupérer les statistiques de l'utilisateur
     * @returns {Promise<Object>}
     */
    async getUserStats() {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/user/stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching user stats:', error);
            throw error;
        }
    }

    /**
     * Récupérer toutes les playlists de l'utilisateur
     * @returns {Promise<Object>}
     */
    async getUserPlaylists() {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching user playlists:', error);
            throw error;
        }
    }

    /**
     * Récupérer les playlists publiques
     * @returns {Promise<Object>}
     */
    async getPublicPlaylists() {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/public`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching public playlists:', error);
            throw error;
        }
    }

    /**
     * Récupérer une playlist spécifique avec ses films
     * @param {number} playlistId - ID de la playlist
     * @returns {Promise<Object>}
     */
    async getPlaylist(playlistId) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/${playlistId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching playlist:', error);
            throw error;
        }
    }

    /**
     * Créer une nouvelle playlist
     * @param {Object} data - { name, description, is_public }
     * @returns {Promise<Object>}
     */
    async createPlaylist(data) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating playlist:', error);
            throw error;
        }
    }

    /**
     * Mettre à jour une playlist
     * @param {number} playlistId - ID de la playlist
     * @param {Object} data - { name, description, is_public }
     * @returns {Promise<Object>}
     */
    async updatePlaylist(playlistId, data) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/${playlistId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating playlist:', error);
            throw error;
        }
    }

    /**
     * Supprimer une playlist
     * @param {number} playlistId - ID de la playlist
     * @returns {Promise<Object>}
     */
    async deletePlaylist(playlistId) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/${playlistId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting playlist:', error);
            throw error;
        }
    }

    /**
     * Ajouter un film à une playlist
     * @param {number} playlistId - ID de la playlist
     * @param {string} movieId - ID du film
     * @returns {Promise<Object>}
     */
    async addMovieToPlaylist(playlistId, movieId) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/${playlistId}/movies`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
                body: JSON.stringify({ movie_id: movieId }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error adding movie to playlist:', error);
            throw error;
        }
    }

    /**
     * Retirer un film d'une playlist
     * @param {number} playlistId - ID de la playlist
     * @param {string} movieId - ID du film
     * @returns {Promise<Object>}
     */
    async removeMovieFromPlaylist(playlistId, movieId) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/${playlistId}/movies/${movieId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error removing movie from playlist:', error);
            throw error;
        }
    }

    /**
     * Réorganiser les films dans une playlist
     * @param {number} playlistId - ID de la playlist
     * @param {Array<string>} movieIds - Tableau des IDs de films dans le nouvel ordre
     * @returns {Promise<Object>}
     */
    async reorderPlaylist(playlistId, movieIds) {
        try {
            const token = this.getToken();
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/playlists/${playlistId}/reorder`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token,
                },
                body: JSON.stringify({ movie_ids: movieIds }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error reordering playlist:', error);
            throw error;
        }
    }
}

// Export d'une instance unique (singleton)
const apiService = new PopMovieAPIService();
export default apiService;